import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getValidationErrors, toFormErrors } from '@/lib/validationErrors';
import { acknowledgementWith } from '@/lib/ruleWarnings';
import {
  mergeBulkEditSummaries,
  summarizeBulkEdit,
  type BulkEditSummary,
  type BulkOperationResult,
  type BulkRowRef,
} from '@/lib/bulkEdit';

/** A run waiting on its warnings: what it came to so far, and what it sent, which the warned rows are re-run with. */
interface Pending<P> {
  summary: BulkEditSummary;
  payload: P;
}

/**
 * One bulk run from a dialog — a bulk edit or a command on the selected rows (docs/15-designspecs/Bulk-Edit-And-Actions.md
 * §8.2–§8.4): sends it with what the dialog holds (`payload`: the fields and values, or the command's input), and either
 * completes with the summary or keeps the warned rows for "Acknowledge and run these N" (D14). That re-runs exactly
 * those rows with the stamps they were selected with, the keys acknowledged and the payload the first run sent — the
 * rows already applied got that, so the warned ones get it too, whatever the dialog shows by then. A run waiting on its
 * warnings has already applied or refused its other rows: closing the dialog then completes it with what it came to,
 * the warned rows left unwritten. A request the server refuses whole (400) touched no row: its messages stay in the
 * dialog for the user to fix.
 */
export function useBulkRun<P>(
  send: (rows: BulkRowRef[], acknowledgedWarnings: string[], payload: P) => Promise<BulkOperationResult>,
  onCompleted: (summary: BulkEditSummary) => void,
) {
  const { t } = useTranslation();
  const [running, setRunning] = useState(false);
  const [requestErrors, setRequestErrors] = useState<string[]>([]);
  const [pending, setPending] = useState<Pending<P> | null>(null);
  const [acknowledged, setAcknowledged] = useState<string[]>([]);

  const reset = () => {
    setRequestErrors([]);
    setPending(null);
    setAcknowledged([]);
  };

  const run = async (rows: BulkRowRef[], acknowledgedWarnings: string[], earlier: BulkEditSummary | null, payload: P) => {
    setRunning(true);
    setRequestErrors([]);
    try {
      const summary = mergeBulkEditSummaries(earlier, summarizeBulkEdit(await send(rows, acknowledgedWarnings, payload)));
      if (summary.warnedRows.length > 0) {
        setPending({ summary, payload });
        return;
      }
      reset();
      onCompleted(summary);
    } catch (error) {
      // A re-run refused whole leaves the run waiting as it was: the warned rows can be tried again, or closed on.
      const errors = getValidationErrors(error);
      setRequestErrors((errors.length > 0 ? errors : toFormErrors(error, t('common.genericError'))).map((e) => e.message));
    } finally {
      setRunning(false);
    }
  };

  const acknowledgeAndRun = () => {
    if (!pending) return;
    const keys = acknowledgementWith(acknowledged, pending.summary.warnings.map((g) => g.warning));
    setAcknowledged(keys);
    // The warned rows were not written: their stamps from the first run still hold.
    void run(pending.summary.warnedRows, keys, { ...pending.summary, warnedRows: [], warnings: [] }, pending.payload);
  };

  /** Closes the dialog: a run waiting on its warnings completes with what it came to, anything else just closes. */
  const close = (onClose: () => void) => {
    const waiting = pending?.summary;
    reset();
    if (waiting) {
      onCompleted(waiting);
      return;
    }
    onClose();
  };

  return {
    running,
    requestErrors,
    /** The run so far while it waits on its warnings; the dialog holds what it sent until then. */
    warned: pending?.summary ?? null,
    /** Runs the selected rows with the payload, carrying the keys already acknowledged in this dialog. */
    start: (rows: BulkRowRef[], payload: P) => void run(rows, acknowledged, null, payload),
    acknowledgeAndRun,
    close,
  };
}
